import React from 'react'
import { get, post } from 'client/utils/api-routes.util'
import {
  PixelButton,
  PixelCurrency,
  PixelDate,
  PixelDiv,
  PixelFactoryContext,
  PixelFlexBox,
  PixelSpinner,
  PixelTag,
  PixelText,
  Section,
  colors,
} from 'pixel-base'
import { AjaxResponse } from 'rxjs-compat'
import styled from 'styled-components'

import { SectionContent } from 'app/common/styles/section'

const ViewPaymentHistory = (props) => {
  const { invoice_id, handleClose } = props
  const {
    showPageLoader,
    hidePageLoader,
    pixelConfirm,
    showSuccess,
    showFailure,
  } = React.useContext<IPixelFactory>(PixelFactoryContext)
  interface IInvoiceData {
    id: number
    ems_customer_id: number
    order_id: number
    amount: number
    refund_id: number
    financial_status: string
    payment_currency: string | null
    stripe_transaction_id: string | null
    created_at: {
      date: string
      timezone_type: number
      timezone: string
    }
  }

  interface IApiResponse {
    data: IInvoiceData[]
  }

  const [showErrorMessage, setShowErrorMessage] = React.useState(false)
  const [paymentHistory, setPaymentHistory] = React.useState<IInvoiceData[]>([])
  const Ref = React.useRef()

  const style = {
    fontSize: '16px',
    fontWeight: '500',
    color: colors.black,
    display: 'table-cell',
    verticalAlign: 'middle',
    paddingLeft: '10px',
    backgroundColor: colors.grayLighter,
    width: '65%',
    wordWrap: 'break-word',
  }

  const labelStyle = {
    fontSize: '16px',
    fontWeight: '600',
    color: colors.grayMedium,
    display: 'table-cell',
    verticalAlign: ' middle',
    paddingRight: '10px',
    padding: ' 10px 10px 0 10px',
    backgroundColor: colors.grayLighter,
  }

  const readData = (invoice_id) => {
    get(`/invoice/${invoice_id}/payment-history`).subscribe(
      (res: AjaxResponse<IApiResponse | any>) => {
        const responseData = res.response as IApiResponse
        if (responseData?.data) {
          setPaymentHistory(responseData?.data || [])
        } else {
          setShowErrorMessage(true)
        }
      },
    )
  }

  const refundInvoiceTransaction = (invoice_id: number, payment_id: number) => {
    handleClose()
    pixelConfirm({
      title: 'Refund Transaction',
      message: 'Are you sure you want to Refund This?',
      onConfirm: () => {
        showPageLoader()
        post(
          `/invoice/${invoice_id}/refund/${payment_id}`,
          undefined,
        ).subscribe((res) => {
          if (res?.response?.data && res?.response?.data?.id) {
            hidePageLoader()
            showSuccess('Transaction is refunded successfully')
          } else if (res?.response?.error) {
            hidePageLoader()
            showFailure(res?.response?.error ?? 'something went wrong')
          }
        })
      },
    })
  }

  React.useEffect(() => {
    readData(invoice_id)
  }, [invoice_id])

  return (
    <Section
      style={
        paymentHistory.length !== 0
          ? {
              overflow: 'auto',
              height: '80vh',
            }
          : {
              overflow: 'auto',
              height: '80vh',
              padding: '0',
              margin: '0',
            }
      }
    >
      {paymentHistory.length !== 0 ? (
        paymentHistory.map((item, index) => {
          return (
            <SectionContent key={index} style={{ marginBottom: '10px' }}>
              <PixelFlexBox flexDirection="column">
                <InfoItem>
                  <PixelText style={labelStyle}>Id</PixelText>
                  <PixelDiv style={style}>
                    {item?.id ? item?.id : '--'}
                  </PixelDiv>
                </InfoItem>
                <InfoItem>
                  <PixelText style={labelStyle}>Amount:</PixelText>
                  <PixelDiv style={style}>
                    <PixelCurrency
                      value={item?.amount ? item.amount : '--'}
                      hideSymbol={false}
                      currency={item?.payment_currency || ''}
                    />
                  </PixelDiv>
                </InfoItem>

                <InfoItem>
                  <PixelText style={labelStyle}>
                    {item?.stripe_transaction_id
                      ? 'Stripe Payment'
                      : 'Payment Recorded'}
                  </PixelText>
                  <PixelDiv style={style}>
                    {item?.stripe_transaction_id
                      ? item.stripe_transaction_id.startsWith('in_')
                        ? 'Paid Via Stripe'
                        : item.stripe_transaction_id.startsWith('re_')
                        ? 'Transaction is Refunded '
                        : 'Paid Via Stripe' // Default case, you can customize this as needed
                      : 'Manually Recorded'}
                  </PixelDiv>
                </InfoItem>

                <InfoItem>
                  <PixelText style={labelStyle}>Paid At:</PixelText>
                  <PixelDiv style={style}>
                    {' '}
                    <PixelDate
                      fromat="dateWithTime"
                      value={new Date(item?.created_at.date)}
                    />
                  </PixelDiv>
                </InfoItem>

                <InfoItem>
                  <PixelText style={labelStyle}>Status:</PixelText>
                  <PixelDiv style={style}>
                    {' '}
                    <PixelTag
                      customBackgroundColor={
                        item?.financial_status == 'paid'
                          ? 'green'
                          : item?.financial_status === 'refund'
                          ? 'gray'
                          : ''
                      }
                    >
                      {item?.financial_status || '--'}
                    </PixelTag>
                  </PixelDiv>
                </InfoItem>

                <InfoItem>
                  {item?.financial_status !== 'refund' &&
                    item?.stripe_transaction_id && (
                      <>
                        <PixelText style={labelStyle}>
                          {item?.stripe_transaction_id
                            ? 'Refund Transaction'
                            : 'Stripe Refunded Transaction Id'}
                        </PixelText>
                        <PixelDiv style={style}>
                          {item?.refund_id == null ? (
                            <PixelButton
                              variant="outline"
                              size="sm"
                              onClick={() => {
                                refundInvoiceTransaction(invoice_id, item?.id)
                              }}
                            >
                              Refund
                            </PixelButton>
                          ) : (
                            <PixelText>
                              {' '}
                              Refunded Transaction: {item?.refund_id}
                            </PixelText>
                          )}
                        </PixelDiv>
                      </>
                    )}
                </InfoItem>
              </PixelFlexBox>
            </SectionContent>
          )
        })
      ) : (
        <Section
          style={{
            height: '100%',
            width: '100%',
            position: 'relative',
            padding: '0',
          }}
          ref={Ref}
        >
          {showErrorMessage ? (
            <PixelFlexBox style={{ justifyContent: 'center' }}>
              <PixelText>There is no record found</PixelText>
            </PixelFlexBox>
          ) : (
            <PixelSpinner ref={Ref} isShow={true} />
          )}
        </Section>
      )}
    </Section>
  )
}
const InfoItem = styled.div`
  display: table;
  table-layout: fixed;
  width: 100%;
  border-spacing: 0;
  border-collapse: collapse;

  &:last-child {
    border-bottom: none;
  }
`

export default ViewPaymentHistory
